import { Checkbox, TextArea } from "@aidkitorg/component-library";
import * as v0 from '@aidkitorg/types/lib/survey';
import { CollectScopeForUser, PermissionScope } from '@aidkitorg/types/lib/translation/permissions';
import { expandTemplates } from '@aidkitorg/types/lib/translation/v0_to_legacy';
import { Author, ChangeSet, generateUserLocation, mergeStateFromEvents, Patch, reconstructStateFromEvents, State, updateUserActivity, UserActivity } from '@aidkitorg/typesheets/lib/collab';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useCollabActions } from './CollabWrapper';
import { ExcludableType, EXCLUDABLE_TYPES_ARRAY, QuestionNodeModal } from "./CondensedSurveyView";
import { InlineEdits, SupportedLanguage, UserInfoContext } from './Context';
import { langToWord, supportedLanguages } from './Localization';
import { stringToColor } from "./Realtime";
import { generateRandomString, nonHookMarkdown, removeDuplicateEvents, SpacedSpinner } from './Util';
import { mapSubsurveyPaths, PathNode, QuestionNodeType, SubsurveyNode } from "./utils/mapSubsurveyPaths";

function QuestionNode(props: {
  questionNode: QuestionNodeType,
  previewLang: SupportedLanguage,
  editorLang: SupportedLanguage,
  displayIndex: string,
  addChange: (textChange: string, lang: SupportedLanguage, trackedObjInfo: v0.TrackedObjectInfo) => void
  isSubQuestion?: boolean,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  // This is to prevent lag between local edits and updates to overall collab state.
  const [localContent, setLocalContent] = useState(props.questionNode.content);

  const otherEditors = (props.questionNode.trackedObjInfo?.[props.editorLang]?.userActivity || [])
    .filter(editor => (editor.uid !== props.questionNode.trackedObjInfo?.[props.editorLang]?.currentAuthor?.uid || editor.tabId !== props.questionNode.trackedObjInfo?.[props.editorLang]?.currentAuthor?.tabId))
    .map((userActivity) => {
      return {
        ...userActivity,
        color: stringToColor(userActivity.name + '/' + userActivity.tabId)
      }
    });

  useEffect(() => {
    const viewingAuthor = props.questionNode.trackedObjInfo?.[props.editorLang]?.currentAuthor;
    const editingAuthorsNotMe = (props.questionNode.trackedObjInfo?.[props.editorLang]?.userActivity || [])
      .some((editingAuthor) => (editingAuthor.uid !== viewingAuthor?.uid || editingAuthor.tabId !== viewingAuthor?.tabId));

    // this can cause cursor jumping issues if we are the one editing, and then our own changes get propogated through.
    // we only really care for this useEffect when someone else is also editing the text field.
    if (editingAuthorsNotMe) {
      if (localContent !== props.questionNode.content) {
        setLocalContent(props.questionNode.content);
      }
    }
  }, [props.questionNode.content, props.editorLang]);

  // Same function used for editor and preview to determine if disabled
  function getElementDisabledAndEmptyText(lang: SupportedLanguage): [boolean, string] {
    const disabled = props.questionNode.trackedObjInfo?.[lang] === undefined;
    let potentialConfigurationIssueWarning = '';
    if (lang !== 'en' && disabled && props.questionNode.trackedObjInfo?.['en'] !== undefined) {
      potentialConfigurationIssueWarning = '⚠️ ';
    }
    return [disabled, `[[${potentialConfigurationIssueWarning}Not configured for this language]]`];
  }

  const [previewDisabled, previewNotConfiguredText] = getElementDisabledAndEmptyText(props.previewLang);
  const [editorDisabled, editorNotConfiguredText] = getElementDisabledAndEmptyText(props.editorLang);

  return (
    <div className='flex justify-between pb-4'>
      <div className='w-1/5 cursor-pointer text-black hover:underline' onClick={() => setIsModalOpen(true)}>
        <span style={{ background: stringToColor(props.questionNode.questionType), borderColor: stringToColor(props.questionNode.questionType, true) }} className='mr-1 rounded-xl px-1 py-[1px] border-[1px] text-xs'>{props.questionNode.questionType}</span>
        <span>{props.displayIndex}</span>
        {props.questionNode.optional && <span className='bg-amber-100 mx-1 rounded-xl px-1 py-[1px] border-[1px] border-amber-200 text-xs'>Optional</span>}
        {props.questionNode.hidden && <span className='bg-amber-100 mx-1 rounded-xl px-1 py-[1px] border-[1px] border-amber-200 text-xs'>Hidden</span>}
        {props.questionNode.targetField && <span> - {props.questionNode.targetField}</span>}
      </div>
      {localContent && (
        <>
          <div className={`rounded-md w-[526px] ${previewDisabled ? 'bg-gray-100' : 'bg-white'} border p-2 ${props.isSubQuestion ? 'ml-16 mr-1 my-1' : 'm-1'}`}>
            {nonHookMarkdown(previewDisabled ? previewNotConfiguredText : localContent[props.previewLang] ?? '')}
          </div>
          <div className='w-1/3'>
            {/* The color box that surrounds an editor when another user is working there */}
            <div className='flex flex-row'>
              {otherEditors.map(ec => {
                return <div key={ec.uid + ec.tabId}
                  style={{
                    position: 'relative',
                    backgroundColor: ec.color,
                    paddingLeft: '2px',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '500',
                    border: `3px solid ${ec.color}`,
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px',
                    height: '20px',
                    width: `${100 / otherEditors.length}%`
                  }}>
                  {ec.name}
                </div>
              })}
            </div>
            {/* The editor component */}
            <div className='h-full' style={(otherEditors.length ? { border: `3px solid ${otherEditors[0].color}`, borderBottomLeftRadius: '10px', borderBottomRightRadius: '10px' } : {})}>
              <TextArea
                label={`Editable text area for question ${props.questionNode.targetField + props.displayIndex}`}
                disabled={editorDisabled}
                className='rounded-md disabled:bg-gray-200 h-full'
                value={editorDisabled ? editorNotConfiguredText : localContent[props.editorLang] ?? ''}
                onChange={(e) => {
                  e.preventDefault();
                  if (!editorDisabled) {
                    setLocalContent({
                      ...localContent,
                      [props.editorLang]: e.target.value
                    });
                    props.addChange(e.target.value, props.editorLang, props.questionNode.trackedObjInfo);
                  }
                }}
              />
            </div>
          </div>
        </>
      )}
      <QuestionNodeModal
        questionType={props.questionNode.questionType}
        displayIndex={props.displayIndex}
        targetField={props.questionNode.targetField}
        choices={props.questionNode.choices}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        content={localContent?.[props.previewLang]}
      />
    </div>
  );
}

function renderSubNodeOuter(params: {
  nodes: PathNode[] | undefined,
  previewLang: SupportedLanguage,
  editorLang: SupportedLanguage,
  excludedTypes: Record<ExcludableType, boolean>,
  addChange: (textChange: string, lang: SupportedLanguage, trackedObjInfo: v0.TrackedObjectInfo) => void,
  prevIndex?: string,
  indexForKey?: number,
  conditionForKey?: string,
}) {

  function renderSubNode(subNode: PathNode, prevIndex: string = '', indexForKey: number, conditionForKey: string = '') {
    if (subNode.type === 'Question') outerCount++;
    const displayIndex = (prevIndex ? prevIndex + '.' + outerCount : outerCount) + ''; // cast to string
    switch (subNode.type) {
      case 'Question':
        if (!params.excludedTypes[subNode.questionType as ExcludableType]) {
          const renderedNodes =
            <QuestionNode
              key={(subNode.targetField || subNode.questionType) + displayIndex + indexForKey + conditionForKey}
              questionNode={subNode}
              previewLang={params.previewLang}
              editorLang={params.editorLang}
              displayIndex={displayIndex}
              addChange={params.addChange}
            />;
          // This conditional handles cases where a question has sub-question content,
          // e.g. a Select question with choices, each with their own content
          if ((subNode.subContent?.length ?? 0) > 0) {
            return [
              renderedNodes,
              ...(subNode.subContent ?? []).map((subQuestion, subQIndex) =>
                <QuestionNode
                  key={(subNode.targetField || subNode.questionType) + displayIndex + indexForKey + conditionForKey + subQIndex}
                  questionNode={subQuestion}
                  previewLang={params.previewLang}
                  editorLang={params.editorLang}
                  displayIndex={displayIndex + '→' + subQIndex}
                  addChange={params.addChange}
                  isSubQuestion={true}
                />
              )
            ];
          }
          return renderedNodes;
        }
        return null;
      case 'Section':
        return <h4 key={subNode.name + displayIndex + indexForKey + conditionForKey} className='mt-2'>
          {subNode.name}
        </h4>;
      case 'Condition':
        // It is possible to have multiple conditions with the same displayIndex and condition
        return <div key={subNode.condition + displayIndex + indexForKey + conditionForKey} className='bg-blue-100 border-t-2 border-l-2 border-slate-300 border-dashed rounded-l-lg'>
          <div className='pl-1 flex justify-between'>{subNode.condition}</div>
          <div className='bg-teal-50 pl-4 relative'>
            {renderSubNodeOuter({
              ...params,
              nodes: (subNode.subNodesTrue || []),
              prevIndex: displayIndex,
              indexForKey,
              conditionForKey: subNode.condition
            })}
          </div>
          {subNode.subNodesFalse !== undefined && (
            <div className='bg-purple-100 pl-4 relative'>
              {renderSubNodeOuter({
                ...params,
                nodes: (subNode.subNodesFalse || []),
                prevIndex: displayIndex,
                indexForKey,
                conditionForKey: subNode.condition,
              })}
            </div>
          )}
        </div>
      default:
        return <div key={displayIndex + indexForKey}>Unknown node type: {subNode.type}</div>;
    }
  }

  let outerCount = 0;
  return (params.nodes || []).flatMap((subNode, i) => renderSubNode(subNode, params.prevIndex, i, params.conditionForKey))
}

export function CopyEditor() {
  const userContext = useContext(UserInfoContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [subsurveyPaths, setSubsurveyPaths] = useState<SubsurveyNode[] | null>(null);
  const [excludedTypes, setExcludedTypes] = useState<Record<ExcludableType, boolean>>({ 'Show Field': true, 'Computed': true, 'Validated': true });
  const [selectedSubsurvey, setSelectedSubsurvey] = useState<'all' | SubsurveyNode | null>(null);
  const [editorLang, setEditorLang] = useState<SupportedLanguage>('en');
  const [previewLang, setPreviewLang] = useState<SupportedLanguage>('en');
  // This is needed because otherwise the closure of mergeEvents has an unfilled version of the userContext,
  // which results in an empty scope being used/returned when the survey needs to update.
  const thisUserRef = useRef<string>();

  // Collab stuff
  const tabId = useRef(generateRandomString(4, 'abcdefghjkmnpqrstuvwxyz123456789'));
  const [author, setAuthor] = useState<Author>();
  const stateRef = useRef<State>();
  const lastActivityTime = useRef(Date.now());
  const convertEventsTimer = useRef<NodeJS.Timeout>();
  // TODO: This might potentially cause cursor jumping.
  const [changeInProgress, setChangeInProgress] = useState(false);

  // State management & actions for Collab Mode 
  const {
    // if collab is not enables, this hook doesn't do much
    collabEnabled,
    // false if collab is actively receiving and merging events, otherwise true
    canPublish,
    // a work in progress. right now, a number indicating how far we've changed from 
    // the last published version
    changesSinceLastPublish,
    // tell listening clients that you just published
    alertJustPublished,
    // refresh collab events and settings
    getCollabState,
    // to be called whenever you make a distro change - this handles sending events in realtime 
    // and also persisting new events to dynamo / s3
    handleNewCollabEvents,
    // updates state var and sets up base events to begin using collab mode.
    // also triggers a refresh on other clients listening browsers.
    enableCollab,
    // updates state var and persisted collab settings.
    // also triggers a refresh on other clients listening browsers.
    disableCollab
  } = useCollabActions(
    'entireprogram',
    tabId.current,
    (events: ChangeSet, userActivity?: UserActivity[]) => {
      mergeEvents(events, userActivity);
    }
  );

  function mergeEvents(events: ChangeSet, userActivity?: UserActivity[]) {
    if (!events.length) {
      return;
    }
    let latestRootTrackedObject;
    if (!stateRef.current) {
      [latestRootTrackedObject, stateRef.current] = reconstructStateFromEvents(events);
    } else {
      // This function and updateUserActivity update the State that is passed into them, so we shouldn't need to reassign
      // stateRef.current here.
      latestRootTrackedObject = mergeStateFromEvents(stateRef.current, events)[0];
      if (userActivity) {
        updateUserActivity(stateRef.current, userActivity);
      }
    }

    // Note: If we called save_survey with this freshestSurvey object, that would be
    // equivalent to the "Real" save that happens upon publishing in Distro
    const freshestSurvey = latestRootTrackedObject.read(true);
    const updatedSubsurveyPaths = updateSubsurveyPaths(freshestSurvey);

    const selectedSubsurveyName =
      (typeof selectedSubsurvey === 'string' && selectedSubsurvey === 'all')
        ? 'all'
        : selectedSubsurvey?.name || window.location.hash.slice(1) || '';

    const prechosenSubSurvey = updatedSubsurveyPaths.find((sp: SubsurveyNode) => sp.name === selectedSubsurveyName)
    setSelectedSubsurvey(prechosenSubSurvey ?? null);

    setChangeInProgress(false);
  }

  function convertInlineEditsToCollabEvents(copyEdits: InlineEdits): ChangeSet {
    let events: ChangeSet = [];
    for (const id of Object.keys(copyEdits.edits || {})) {
      for (const lang of Object.keys((copyEdits.edits || {})[id] || {})) {
        // Create Collab "patch" operation
        const edit = copyEdits.edits[id]![(lang as SupportedLanguage)];
        const op: Patch = {
          op: 'patch',
          id: Math.random().toString(), // TODO: should we be using uuids here? Collab doesn't but that doesn't mean no.
          value: edit.value,
          prev_gen: Math.max(0, edit.prev_gen),
          author
        };
        events.push([id, 'primitive', op]);
      }
    }
    events = removeDuplicateEvents(events);
    return events;
  }

  /**
   * Traverses an entire progrogram config and constructs (via mapSubsurveyPaths()) the paths
   * one could take while completing it, branching at conditionals, as a tree of renderable nodes
   */
  function updateSubsurveyPaths(survey: v0.Root): SubsurveyNode[] {
    const expandedSurvey = expandTemplates(survey) as v0.ExpandedSurvey;
    let scope: PermissionScope | undefined;
    if ('users' in expandedSurvey) {
      scope = CollectScopeForUser(thisUserRef.current!, expandedSurvey);
    }
    const subSurveyPaths = mapSubsurveyPaths(expandedSurvey.survey, scope);
    setSubsurveyPaths(subSurveyPaths);
    return subSurveyPaths;
  }

  // General Initialization
  useEffect(() => {
    if (subsurveyPaths !== null || !userContext.uid) {
      return;
    }

    thisUserRef.current = userContext.uid;

    if ((userContext?.tags ?? []).some((tag) => ['inline_edit', 'admin'].includes(tag))) {
      if (!author) {
        setAuthor({ uid: userContext.uid, tags: userContext.tags, tabId: tabId.current });
      }
      setIsLoading(true);
      (async () => {
        const resp = await getCollabState();
        const [rootTrackedObject, state] = reconstructStateFromEvents(resp.events);
        stateRef.current = state;
        stateRef.current.currentAuthor = { uid: userContext.uid, tags: userContext.tags, tabId: tabId.current }
        const surveyFromCollabEvents = rootTrackedObject.read(true);
        updateSubsurveyPaths(surveyFromCollabEvents);
        setIsLoading(false);
      })();
    }

  }, [subsurveyPaths, userContext]);

  // TODO: Double check that this even needs to be a useEffect
  useEffect(() => {
    const surveyFromSearchParams = window.location.hash.slice(1) || '';
    if (subsurveyPaths && surveyFromSearchParams) {
      const prechosenSubSurvey = surveyFromSearchParams === 'all' ? 'all' : subsurveyPaths.find((sp: SubsurveyNode) => sp.name === surveyFromSearchParams)
      setSelectedSubsurvey(prechosenSubSurvey ?? null);
    }
  }, [subsurveyPaths]);

  // Somewhat analogous to Config.tsx's handleUpdatesInner(), i.e. a function
  // that is called whenever there is an update, in this case to "edits"
  // This is how the various children update the collab state.
  const addChange = useCallback((textChange: string, lang: SupportedLanguage, trackedObjInfo: v0.TrackedObjectInfo) => {
    if (!trackedObjInfo[lang]) {
      // TODO: if this is possible, log an error.
      return;
    }
    const { id, prev_gen } = trackedObjInfo[lang]!;

    if (collabEnabled) {
      const changesToEdits = {
        edits: {
          [id]: {
            [lang]: { value: textChange, prev_gen: prev_gen }
          },
        },
        latestEditedObjectId: id
      } as InlineEdits;

      if (convertEventsTimer.current) {
        clearTimeout(convertEventsTimer.current);
      }

      // When we convert events to collab, they count as the same generation. We thus have to only convert them once, 
      // so we only have the latest version of that generation (most recent edit)
      convertEventsTimer.current = setTimeout(() => {
        setChangeInProgress(true);
        if (stateRef.current && author && changesToEdits.latestEditedObjectId) {
          stateRef.current.myActivity = generateUserLocation(author, changesToEdits.latestEditedObjectId);
        }
        const copyEditsAsCollabChangeSet = convertInlineEditsToCollabEvents(changesToEdits);
        handleNewCollabEvents(copyEditsAsCollabChangeSet, stateRef.current?.myActivity, stateRef.current);
      }, 750);
    }
  }, [handleNewCollabEvents]);


  // Rendering options split out into if/else to avoid nested ternaries
  if (isLoading || subsurveyPaths === null) {
    return (
      <div className='m-2'>
        <span className='text-[2rem] font-medium text-gray-900 py-3'>{'Copy Editor is loading... '}</span><SpacedSpinner className='text-sm p-2 mb-[2px] ml-2' />
      </div>
    );
  } else if (collabEnabled === false) {
    return (
      <div className='m-2 text-sm font-bold text-gray-900'>
        {'Copy Editor requires Collab to be enabled. Enable it, or ask and Admin to do so'}
      </div>
    )
  }

  // The editor, once loaded and confirmed collab is enabled
  return (
    <div className='w-max'>
      <div className='z-10 sticky top-0 left-0 bg-white w-screen'> {/* z-index here to ensure dropdown stays on top */}
        <div className='flex py-2 shadow-md'>
          <h2 className='px-2 py-2'>{'Copy Editor'}</h2>
          {/* Dropdown for selecting Survey to edit */}
          <div className='ml-2 mr-3 min-w-[200px]'>
            <label htmlFor="subsurvey-select" className="block mb-2 text-sm font-bold text-gray-900">{'Select survey'}</label>
            <select
              id="subsurvey-select"
              value={!!selectedSubsurvey ? (selectedSubsurvey === 'all' ? 'all' : selectedSubsurvey.name) : ''}
              onChange={(e) => {
                if (e.target.value === 'All Subsurveys') {
                  setSelectedSubsurvey('all');
                  window.location.hash = '#all';
                } else {
                  setSelectedSubsurvey(subsurveyPaths.find((subsurvey) => subsurvey.name === e.target.value)!);
                  window.location.hash = '#' + e.target.value;
                }
              }}
              className="mt-1 block px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 w-[200px]"
              aria-label="Select subsurvey to edit"
            >
              {[{ name: 'All Subsurveys' }, ...subsurveyPaths].map((subsurvey) => (
                <option key={subsurvey.name} value={subsurvey.name}>
                  {subsurvey.name}
                </option>
              ))}
            </select>
          </div>
          {/* Toggles for showing/hiding certain Question Types */}
          <div className='mx-3 min-w-fit'>
            <fieldset className='grid grid-cols-2 grid-rows-2'>
              <legend className='text-sm font-bold text-gray-900'>
                Exclude Additional Question Types
              </legend>
              {EXCLUDABLE_TYPES_ARRAY.map((t) =>
                <Checkbox
                  key={t}
                  checked={excludedTypes[t]}
                  onChange={(e) => {
                    setExcludedTypes(prevState => {
                      const newExcludedTypes = { ...prevState }
                      newExcludedTypes[t] = !newExcludedTypes[t];
                      return newExcludedTypes;
                    });
                  }}
                  label={t}
                />
              )}
            </fieldset>
          </div>
          {/* Preview column language selector */}
          <div className='mx-3 w-[200px]'>
            <label htmlFor="language-select" className="block mb-2 text-sm font-bold text-gray-900">{'Select preview language'}</label>
            <select
              id="preview-language-select"
              value={previewLang}
              onChange={(e) => {
                setPreviewLang(e.target.value as SupportedLanguage);
              }}
              className="mt-1 block px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 w-[200px]"
              aria-label="Select preview language"
            >
              {Object.keys(supportedLanguages).map((lang) => (
                <option key={lang} value={lang}>
                  {supportedLanguages[lang as SupportedLanguage]} {lang !== 'en' ? '(' + langToWord(lang as SupportedLanguage, 'Name') + ')' : null}
                </option>
              ))}
            </select>
          </div>
          {/* Editor column language selector */}
          <div className='mx-3 w-[200px]'>
            <label htmlFor="language-select" className="block mb-2 text-sm font-bold text-gray-900">{'Select editor language'}</label>
            <select
              id="editor-language-select"
              value={editorLang}
              onChange={(e) => {
                setEditorLang(e.target.value as SupportedLanguage);
              }}
              className="mt-1 block px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 w-[200px]"
              aria-label="Select editor language"
            >
              {Object.keys(supportedLanguages).map((lang) => (
                <option key={lang} value={lang}>
                  {supportedLanguages[lang as SupportedLanguage]} {lang !== 'en' ? '(' + langToWord(lang as SupportedLanguage, 'Name') + ')' : null}
                </option>
              ))}
            </select>
          </div>
          {/* TODO: Cannot use FacePile yet because it causes too many rerenders, like due to "activeRequests" */}
          {/* <div>
              {'Other users here:'}
              <FacePile name={userContext.uid!} channel={get_deployment() + ':entireprogram'} browserTab={tabId.current} />
            </div> */}
          <div className='mx-3 text-sm font-normal text-gray-700'>
            {changeInProgress ? 'Saving changes...' : 'All changes saved'}
          </div>
        </div>
      </div>
      {/* This is where the survey/editor is rendered */}
      {selectedSubsurvey &&
        <div className='flex p-2 bg-gray-100'>
          {((typeof selectedSubsurvey === 'string' && selectedSubsurvey === 'all') ? subsurveyPaths : [selectedSubsurvey]).map((subsurveyNode, i) => {
            return (
              <div className='w-64 m-2 h-max p-2' style={{ minWidth: '1600px' }} key={subsurveyNode.name + i}>
                <h2>{subsurveyNode.name}</h2>
                {renderSubNodeOuter({
                  nodes: subsurveyNode.subNodes,
                  excludedTypes,
                  addChange,
                  previewLang,
                  editorLang
                })}
              </div>
            )
          })}
        </div>
      }
    </div>
  );
}
